import { searchHotels as searchHotelsHelper } from './searchHelpers';

// Helper functions for search handlers

/**
 * Debounce function for search
 * @param {Function} func - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} - The debounced function
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * Handle search input change
 * @param {Object} e - The event object
 * @param {Function} setSearchTerm - State setter for search term
 * @param {Function} debouncedSearchCities - The debounced search function
 */
export const handleSearchChange = (e, setSearchTerm, debouncedSearchCities) => {
  const value = e.target.value;
  setSearchTerm(value);
  
  debouncedSearchCities(value);
};

/**
 * Handle search submit
 * @param {Object} e - The event object
 */
export const handleSearchSubmit = (e) => {
  e.preventDefault();
  // This function can be expanded to handle search form submission with dates if needed
};

/**
 * Select a city from search results
 * @param {Object} city - The selected city object
 * @param {Function} setSelectedCity - State setter for selected city
 * @param {Function} setSearchTerm - State setter for search term
 * @param {Function} setCitySearchResults - State setter for city search results
 * @param {Function} setShowCitySearchResults - State setter for showing city search results
 * @param {Function} searchHotels - Function to search hotels
 */
export const selectCityFromSearch = (city, setSelectedCity, setSearchTerm, setCitySearchResults, setShowCitySearchResults) => {
  setSelectedCity(city);
  setSearchTerm(`${city.city_name}, ${city.country_name}`);
  setCitySearchResults([]);
  setShowCitySearchResults(false);
  // Don't automatically search - let user click the search button
};

/**
 * Select a hotel from search results
 * @param {Object} hotel - The hotel object from search results
 * @param {Object} selectedCity - The selected city object
 * @param {Function} setHotelFormData - State setter for hotel form data
 * @param {Function} setShowHotelForm - State setter for showing hotel form
 * @param {Function} setShowSearchResults - State setter for showing search results
 * @param {Function} setSearchTerm - State setter for search term
 * @param {Function} setSearchResults - State setter for search results
 */
export const selectHotelFromSearch = (hotel, selectedCity, setHotelFormData, setShowHotelForm, setShowSearchResults, setSearchTerm, setSearchResults) => {
  setHotelFormData(prev => ({
    ...prev,
    name_en: hotel.HotelName || hotel.hotelName || '',
    // Initialize with empty accessibility template IDs
    accessibility_template_ids: [],
    featured: false,
    city: selectedCity?.city_name || '',
    country: selectedCity?.country_name || '',
    images_urls: [''], // Don't pre-fill images when adding a new hotel
    supplier_id: hotel.HotelCode || '', // Add supplier_id from HotelCode
    commission_percentage: hotel.CommissionPercentage || hotel.commissionPercentage || 10 // Add commission_percentage or default to 10
  }));
  
  setShowHotelForm(true);
  setShowSearchResults(false);
  setSearchTerm('');
  setSearchResults([]);
};

export default {
  debounce,
  handleSearchChange,
  handleSearchSubmit,
  selectCityFromSearch,
  selectHotelFromSearch
};